import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/authService";
import {
  blogService,
  newsService,
  serviceService,
  productService,
  getLocalizedString,
} from "../../services/firebaseService";
import {
  BlogPost,
  NewsItem,
  Service,
  Product,
} from "../../services/firebaseService";
import HeroAdmin from "../../components/admin/HeroAdmin";
import { useTheme } from "../../contexts/ThemeContext";

const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "dashboard" | "blogs" | "news" | "services" | "products" | "hero"
  >("dashboard");
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === 'dark';

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
        loadData();
      } else {
        navigate("/admin/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [blogsData, newsData, servicesData, productsData] =
        await Promise.all([
          blogService.getAllBlogPosts(),
          newsService.getAllNewsItems(),
          serviceService.getAllServices(),
          productService.getAllProducts(),
        ]);
      setBlogs(blogsData);
      setNews(newsData);
      setServices(servicesData);
      setProducts(productsData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.signOut();
      navigate("/admin/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleDeleteBlog = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this blog post?")) {
      try {
        await blogService.deleteBlogPost(id);
        setBlogs(blogs.filter((blog) => blog.id !== id));
      } catch (error) {
        console.error("Error deleting blog post:", error);
      }
    }
  };

  const handleDeleteNews = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this news item?")) {
      try {
        await newsService.deleteNewsItem(id);
        setNews(news.filter((item) => item.id !== id));
      } catch (error) {
        console.error("Error deleting news item:", error);
      }
    }
  };

  const togglePublishBlog = async (blog: BlogPost) => {
    try {
      await blogService.updateBlogPost(blog.id!, {
        published: !blog.published,
      });
      setBlogs(
        blogs.map((b) =>
          b.id === blog.id ? { ...b, published: !b.published } : b
        )
      );
    } catch (error) {
      console.error("Error updating blog post:", error);
    }
  };

  const togglePublishNews = async (newsItem: NewsItem) => {
    try {
      await newsService.updateNewsItem(newsItem.id!, {
        published: !newsItem.published,
      });
      setNews(
        news.map((n) =>
          n.id === newsItem.id ? { ...n, published: !n.published } : n
        )
      );
    } catch (error) {
      console.error("Error updating news item:", error);
    }
  };

  // Service handlers
  const handleDeleteService = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this service?")) {
      try {
        await serviceService.deleteService(id);
        setServices(services.filter((service) => service.id !== id));
      } catch (error) {
        console.error("Error deleting service:", error);
      }
    }
  };

  const togglePublishService = async (service: Service) => {
    try {
      await serviceService.updateService(service.id!, {
        published: !service.published,
      });
      setServices(
        services.map((s) =>
          s.id === service.id ? { ...s, published: !s.published } : s
        )
      );
    } catch (error) {
      console.error("Error updating service:", error);
    }
  };

  // Product handlers
  const handleDeleteProduct = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await productService.deleteProduct(id);
        setProducts(products.filter((product) => product.id !== id));
      } catch (error) {
        console.error("Error deleting product:", error);
      }
    }
  };

  const togglePublishProduct = async (product: Product) => {
    try {
      await productService.updateProduct(product.id!, {
        published: !product.published,
      });
      setProducts(
        products.map((p) =>
          p.id === product.id ? { ...p, published: !p.published } : p
        )
      );
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--gradient-primary)] p-0 flex flex-col">
        <div className="flex justify-center items-center p-12 min-h-[200px]">
          <div className="flex flex-col items-center gap-4">
            <div className={`w-10 h-10 border-4 ${isDark ? 'border-white/30 border-t-white' : 'border-gray-200 border-t-primary'} rounded-full animate-spin`}></div>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg`}>Loading admin panel...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--gradient-primary)] p-0 flex flex-col">
      <div className={`flex justify-between items-center px-8 py-8 ${isDark ? 'text-white border-white/20' : 'text-gray-900 border-gray-200'} backdrop-blur-lg border-b md:px-4 md:flex-col md:items-start md:gap-4`}>
        <div>
          <h1 className="text-[2.5rem] mb-2 font-bold md:text-2xl">
            Admin Panel
          </h1>
          <p className={`text-lg ${isDark ? 'opacity-90' : 'opacity-80'} md:text-base`}>
            Welcome, {user?.displayName || user?.email}
          </p>
        </div>
        <button
          className={`px-6 py-3 text-sm rounded-lg border-none cursor-pointer transition-all duration-200 ${isDark ? 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/40' : 'bg-gray-100 border-gray-300 text-gray-900 hover:bg-gray-200 hover:border-gray-400'} border backdrop-blur-lg hover:-translate-y-0.5`}
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>

      <div className={`flex justify-center gap-2 px-8 py-4 backdrop-blur-lg border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex-wrap md:px-4 md:flex-col md:items-center`}>
        <button
          className={`px-5 py-3 rounded-lg cursor-pointer transition-all duration-300 font-medium backdrop-blur-lg text-sm min-w-[120px] text-center ${
            activeTab === "dashboard"
              ? isDark ? "bg-white/90 text-primary border border-white shadow-[0_4px_15px_rgba(0,0,0,0.2)]" : "bg-primary text-white border border-primary shadow-[0_4px_15px_rgba(37,99,235,0.3)]"
              : isDark ? "bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/40" : "bg-gray-100 border border-gray-300 text-gray-900 hover:bg-gray-200 hover:border-gray-400"
          } hover:-translate-y-0.5 md:w-full md:max-w-[300px]`}
          onClick={() => setActiveTab("dashboard")}
        >
          Dashboard
        </button>
        <button
          className={`px-5 py-3 rounded-lg cursor-pointer transition-all duration-300 font-medium backdrop-blur-lg text-sm min-w-[120px] text-center ${
            activeTab === "blogs"
              ? isDark ? "bg-white/90 text-primary border border-white shadow-[0_4px_15px_rgba(0,0,0,0.2)]" : "bg-primary text-white border border-primary shadow-[0_4px_15px_rgba(37,99,235,0.3)]"
              : isDark ? "bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/40" : "bg-gray-100 border border-gray-300 text-gray-900 hover:bg-gray-200 hover:border-gray-400"
          } hover:-translate-y-0.5 md:w-full md:max-w-[300px]`}
          onClick={() => setActiveTab("blogs")}
        >
          Blogs ({blogs.length})
        </button>
        <button
          className={`px-5 py-3 rounded-lg cursor-pointer transition-all duration-300 font-medium backdrop-blur-lg text-sm min-w-[120px] text-center ${
            activeTab === "news"
              ? isDark ? "bg-white/90 text-primary border border-white shadow-[0_4px_15px_rgba(0,0,0,0.2)]" : "bg-primary text-white border border-primary shadow-[0_4px_15px_rgba(37,99,235,0.3)]"
              : isDark ? "bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/40" : "bg-gray-100 border border-gray-300 text-gray-900 hover:bg-gray-200 hover:border-gray-400"
          } hover:-translate-y-0.5 md:w-full md:max-w-[300px]`}
          onClick={() => setActiveTab("news")}
        >
          News ({news.length})
        </button>
        <button
          className={`px-5 py-3 rounded-lg cursor-pointer transition-all duration-300 font-medium backdrop-blur-lg text-sm min-w-[120px] text-center ${
            activeTab === "services"
              ? isDark ? "bg-white/90 text-primary border border-white shadow-[0_4px_15px_rgba(0,0,0,0.2)]" : "bg-primary text-white border border-primary shadow-[0_4px_15px_rgba(37,99,235,0.3)]"
              : isDark ? "bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/40" : "bg-gray-100 border border-gray-300 text-gray-900 hover:bg-gray-200 hover:border-gray-400"
          } hover:-translate-y-0.5 md:w-full md:max-w-[300px]`}
          onClick={() => setActiveTab("services")}
        >
          Services ({services.length})
        </button>
        <button
          className={`px-5 py-3 rounded-lg cursor-pointer transition-all duration-300 font-medium backdrop-blur-lg text-sm min-w-[120px] text-center ${
            activeTab === "products"
              ? isDark ? "bg-white/90 text-primary border border-white shadow-[0_4px_15px_rgba(0,0,0,0.2)]" : "bg-primary text-white border border-primary shadow-[0_4px_15px_rgba(37,99,235,0.3)]"
              : isDark ? "bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/40" : "bg-gray-100 border border-gray-300 text-gray-900 hover:bg-gray-200 hover:border-gray-400"
          } hover:-translate-y-0.5 md:w-full md:max-w-[300px]`}
          onClick={() => setActiveTab("products")}
        >
          Products ({products.length})
        </button>
        <button
          className={`px-5 py-3 rounded-lg cursor-pointer transition-all duration-300 font-medium backdrop-blur-lg text-sm min-w-[120px] text-center ${
            activeTab === "hero"
              ? isDark ? "bg-white/90 text-primary border border-white shadow-[0_4px_15px_rgba(0,0,0,0.2)]" : "bg-primary text-white border border-primary shadow-[0_4px_15px_rgba(37,99,235,0.3)]"
              : isDark ? "bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/40" : "bg-gray-100 border border-gray-300 text-gray-900 hover:bg-gray-200 hover:border-gray-400"
          } hover:-translate-y-0.5 md:w-full md:max-w-[300px]`}
          onClick={() => setActiveTab("hero")}
        >
          Hero Slides
        </button>
        <button 
          className={`px-5 py-3 rounded-lg cursor-pointer transition-all duration-300 font-medium backdrop-blur-lg text-sm min-w-[120px] text-center ${isDark ? 'bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/40' : 'bg-gray-100 border border-gray-300 text-gray-900 hover:bg-gray-200 hover:border-gray-400'} hover:-translate-y-0.5 md:w-full md:max-w-[300px]`}
          onClick={() => navigate("/admin-padma/voting")}
        >
          Go to Padma Admin
        </button>
      </div>

      <div className={`rounded-none p-8 shadow-none min-h-[calc(100vh-200px)] flex-1 m-0 md:p-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {activeTab === "dashboard" && (
          <div className="w-full">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-6 mb-8">
              <div className={`${isDark ? 'bg-white/10 backdrop-blur-sm border-white/20' : 'bg-white border-gray-200'} rounded-xl p-6 shadow-[0_4px_6px_rgba(0,0,0,0.05)] border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(0,0,0,0.1)]`}>
                <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg font-semibold m-0 mb-4 flex items-center gap-2 before:content-[''] before:w-[3px] before:h-5 ${isDark ? 'before:bg-white' : 'before:bg-primary'} before:rounded-sm`}>
                  📝 Blog Posts
                </h3>
                <div className="flex justify-between items-center mt-4">
                  <div className={`text-[2rem] font-bold ${isDark ? 'text-white' : 'text-primary'}`}>
                    {blogs.length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-600'} uppercase tracking-wider`}>
                    Total Posts
                  </div>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <div className={`text-[2rem] font-bold ${isDark ? 'text-white' : 'text-primary'}`}>
                    {blogs.filter((blog) => blog.published).length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-600'} uppercase tracking-wider`}>
                    Published
                  </div>
                </div>
              </div>

              <div className={`${isDark ? 'bg-white/10 backdrop-blur-sm border-white/20' : 'bg-white border-gray-200'} rounded-xl p-6 shadow-[0_4px_6px_rgba(0,0,0,0.05)] border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(0,0,0,0.1)]`}>
                <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg font-semibold m-0 mb-4 flex items-center gap-2 before:content-[''] before:w-[3px] before:h-5 ${isDark ? 'before:bg-white' : 'before:bg-primary'} before:rounded-sm`}>
                  📰 News Items
                </h3>
                <div className="flex justify-between items-center mt-4">
                  <div className={`text-[2rem] font-bold ${isDark ? 'text-white' : 'text-primary'}`}>
                    {news.length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-600'} uppercase tracking-wider`}>
                    Total Items
                  </div>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <div className={`text-[2rem] font-bold ${isDark ? 'text-white' : 'text-primary'}`}>
                    {news.filter((item) => item.published).length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-600'} uppercase tracking-wider`}>
                    Published
                  </div>
                </div>
              </div>

              <div className={`${isDark ? 'bg-white/10 backdrop-blur-sm border-white/20' : 'bg-white border-gray-200'} rounded-xl p-6 shadow-[0_4px_6px_rgba(0,0,0,0.05)] border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(0,0,0,0.1)]`}>
                <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg font-semibold m-0 mb-4 flex items-center gap-2 before:content-[''] before:w-[3px] before:h-5 ${isDark ? 'before:bg-white' : 'before:bg-primary'} before:rounded-sm`}>
                  🛠️ Services
                </h3>
                <div className="flex justify-between items-center mt-4">
                  <div className={`text-[2rem] font-bold ${isDark ? 'text-white' : 'text-primary'}`}>
                    {services.length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-600'} uppercase tracking-wider`}>
                    Total Services
                  </div>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <div className={`text-[2rem] font-bold ${isDark ? 'text-white' : 'text-primary'}`}>
                    {services.filter((service) => service.published).length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-600'} uppercase tracking-wider`}>
                    Published
                  </div>
                </div>
              </div>

              <div className={`${isDark ? 'bg-white/10 backdrop-blur-sm border-white/20' : 'bg-white border-gray-200'} rounded-xl p-6 shadow-[0_4px_6px_rgba(0,0,0,0.05)] border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(0,0,0,0.1)]`}>
                <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg font-semibold m-0 mb-4 flex items-center gap-2 before:content-[''] before:w-[3px] before:h-5 ${isDark ? 'before:bg-white' : 'before:bg-primary'} before:rounded-sm`}>
                  📦 Products
                </h3>
                <div className="flex justify-between items-center mt-4">
                  <div className={`text-[2rem] font-bold ${isDark ? 'text-white' : 'text-primary'}`}>
                    {products.length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-600'} uppercase tracking-wider`}>
                    Total Products
                  </div>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <div className={`text-[2rem] font-bold ${isDark ? 'text-white' : 'text-primary'}`}>
                    {products.filter((product) => product.published).length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-600'} uppercase tracking-wider`}>
                    Published
                  </div>
                </div>
              </div>

              <div className={`${isDark ? 'bg-white/10 backdrop-blur-sm border-white/20' : 'bg-white border-gray-200'} rounded-xl p-6 shadow-[0_4px_6px_rgba(0,0,0,0.05)] border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(0,0,0,0.1)]`}>
                <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg font-semibold m-0 mb-4 flex items-center gap-2 before:content-[''] before:w-[3px] before:h-5 ${isDark ? 'before:bg-white' : 'before:bg-primary'} before:rounded-sm`}>
                  📊 Content Overview
                </h3>
                <div className="flex justify-between items-center mt-4">
                  <div className={`text-[2rem] font-bold ${isDark ? 'text-white' : 'text-primary'}`}>
                    {blogs.length +
                      news.length +
                      services.length +
                      products.length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-600'} uppercase tracking-wider`}>
                    Total Content
                  </div>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <div className={`text-[2rem] font-bold ${isDark ? 'text-white' : 'text-primary'}`}>
                    {blogs.filter((blog) => blog.published).length +
                      news.filter((item) => item.published).length +
                      services.filter((service) => service.published).length +
                      products.filter((product) => product.published).length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-600'} uppercase tracking-wider`}>
                    Live Content
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "blogs" && (
          <div className="w-full">
            <div className={`flex justify-between items-center mb-8 py-6 border-b-2 ${isDark ? 'border-white/20' : 'border-gray-200'} rounded-t-xl px-8 -mx-8 -mt-8 md:flex-col md:items-start md:gap-4 md:px-4`}>
              <h2 className={`${isDark ? 'text-white' : 'text-gray-900'} text-2xl font-bold m-0 flex items-center gap-3 before:content-[''] before:w-1 before:h-6 ${isDark ? 'before:bg-white' : 'before:bg-primary'} before:rounded-sm`}>
                Blog Posts Management
              </h2>
              <button
                className="px-5 py-2.5 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm bg-[var(--gradient-primary)] text-black shadow-[0_2px_4px_rgba(37,99,235,0.2)] hover:bg-[var(--gradient-secondary)] hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(37,99,235,0.3)]"
                onClick={() => navigate("/admin/blog/new")}
              >
                Add New Blog Post
              </button>
            </div>

            <div className={`overflow-x-auto rounded-xl border ${isDark ? 'border-white/20 bg-white/10 backdrop-blur-sm' : 'border-gray-200 bg-white'} shadow-[0_4px_6px_rgba(0,0,0,0.05)] mt-4`}>
              <table className={`w-full border-collapse my-4 rounded-xl overflow-hidden shadow-[0_4px_6px_rgba(0,0,0,0.05)] border ${isDark ? 'border-white/20' : 'border-gray-200'} md:text-sm`}>
                <thead>
                  <tr>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Title
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Author
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Status
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Featured
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Created
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map((blog) => (
                    <tr key={blog.id} className={isDark ? "hover:bg-white/10" : "hover:bg-gray-50"}>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <div>
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {getLocalizedString(blog.title, "en")}
                          </div>
                          <div className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'} line-clamp-2`}>
                            {getLocalizedString(blog.excerpt, "en")}
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {blog.author}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
                            blog.published
                              ? isDark ? "bg-green-500/80 text-white" : "bg-green-100 text-green-800"
                              : isDark ? "bg-yellow-500/80 text-white" : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {blog.published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
                            blog.featured
                              ? isDark ? "bg-green-500/80 text-white" : "bg-green-100 text-green-800"
                              : isDark ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {blog.featured ? "Featured" : "Regular"}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {blog.createdAt?.toDate().toLocaleDateString()}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <div className="flex gap-2 flex-wrap items-center md:flex-col">
                          <button
                            className={`px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm ${isDark ? 'bg-white/20 text-white border border-white/30 hover:bg-white/30' : 'bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100'} hover:-translate-y-0.5`}
                            onClick={() =>
                              navigate(`/admin/blog/edit/${blog.id}`)
                            }
                          >
                            Edit
                          </button>
                          <button
                            className={`px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm ${
                              blog.published
                                ? "bg-amber-500 text-white hover:bg-amber-600"
                                : "bg-green-500 text-white hover:bg-green-600"
                            } hover:-translate-y-0.5`}
                            onClick={() => togglePublishBlog(blog)}
                          >
                            {blog.published ? "Unpublish" : "Publish"}
                          </button>
                          <button
                            className="px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm bg-gradient-to-r from-red-500 to-red-600 text-white shadow-[0_2px_4px_rgba(239,68,68,0.2)] hover:from-red-600 hover:to-red-700 hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(239,68,68,0.3)]"
                            onClick={() => handleDeleteBlog(blog.id!)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "news" && (
          <div className="w-full">
            <div className={`flex justify-between items-center mb-8 py-6 border-b-2 ${isDark ? 'border-white/20' : 'border-gray-200'} rounded-t-xl px-8 -mx-8 -mt-8 md:flex-col md:items-start md:gap-4 md:px-4`}>
              <h2 className={`${isDark ? 'text-white' : 'text-gray-900'} text-2xl font-bold m-0 flex items-center gap-3 before:content-[''] before:w-1 before:h-6 ${isDark ? 'before:bg-white' : 'before:bg-primary'} before:rounded-sm`}>
                News Management
              </h2>
              <button
                className="px-5 py-2.5 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm bg-[var(--gradient-primary)] text-black shadow-[0_2px_4px_rgba(37,99,235,0.2)] hover:bg-[var(--gradient-secondary)] hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(37,99,235,0.3)]"
                onClick={() => navigate("/admin/news/new")}
              >
                Add New News Item
              </button>
            </div>

            <div className={`overflow-x-auto rounded-xl border ${isDark ? 'border-white/20 bg-white/10 backdrop-blur-sm' : 'border-gray-200 bg-white'} shadow-[0_4px_6px_rgba(0,0,0,0.05)] mt-4`}>
              <table className={`w-full border-collapse my-4 rounded-xl overflow-hidden shadow-[0_4px_6px_rgba(0,0,0,0.05)] border ${isDark ? 'border-white/20' : 'border-gray-200'} md:text-sm`}>
                <thead>
                  <tr>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Title
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Author
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Category
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Status
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Featured
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Created
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {news.map((newsItem) => (
                    <tr key={newsItem.id} className={isDark ? "hover:bg-white/10" : "hover:bg-gray-50"}>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <div>
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {getLocalizedString(newsItem.title, "en")}
                          </div>
                          <div className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'} line-clamp-2`}>
                            {getLocalizedString(newsItem.excerpt, "en")}
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {newsItem.author}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {newsItem.category}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
                            newsItem.published
                              ? isDark ? "bg-green-500/80 text-white" : "bg-green-100 text-green-800"
                              : isDark ? "bg-yellow-500/80 text-white" : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {newsItem.published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
                            newsItem.featured
                              ? isDark ? "bg-green-500/80 text-white" : "bg-green-100 text-green-800"
                              : isDark ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {newsItem.featured ? "Featured" : "Regular"}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {newsItem.createdAt?.toDate().toLocaleDateString()}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <div className="flex gap-2 flex-wrap items-center md:flex-col">
                          <button
                            className={`px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm ${isDark ? 'bg-white/20 text-white border border-white/30 hover:bg-white/30' : 'bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100'} hover:-translate-y-0.5`}
                            onClick={() =>
                              navigate(`/admin/news/edit/${newsItem.id}`)
                            }
                          >
                            Edit
                          </button>
                          <button
                            className={`px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm ${
                              newsItem.published
                                ? "bg-amber-500 text-white hover:bg-amber-600"
                                : "bg-green-500 text-white hover:bg-green-600"
                            } hover:-translate-y-0.5`}
                            onClick={() => togglePublishNews(newsItem)}
                          >
                            {newsItem.published ? "Unpublish" : "Publish"}
                          </button>
                          <button
                            className="px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm bg-gradient-to-r from-red-500 to-red-600 text-white shadow-[0_2px_4px_rgba(239,68,68,0.2)] hover:from-red-600 hover:to-red-700 hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(239,68,68,0.3)]"
                            onClick={() => handleDeleteNews(newsItem.id!)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "services" && (
          <div className="w-full">
            <div className={`flex justify-between items-center mb-8 py-6 border-b-2 ${isDark ? 'border-white/20' : 'border-gray-200'} rounded-t-xl px-8 -mx-8 -mt-8 md:flex-col md:items-start md:gap-4 md:px-4`}>
              <h2 className={`${isDark ? 'text-white' : 'text-gray-900'} text-2xl font-bold m-0 flex items-center gap-3 before:content-[''] before:w-1 before:h-6 ${isDark ? 'before:bg-white' : 'before:bg-primary'} before:rounded-sm`}>
                Services Management
              </h2>
              <button
                className="px-5 py-2.5 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm bg-[var(--gradient-primary)] text-black shadow-[0_2px_4px_rgba(37,99,235,0.2)] hover:bg-[var(--gradient-secondary)] hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(37,99,235,0.3)]"
                onClick={() => navigate("/admin/service/new")}
              >
                Add New Service
              </button>
            </div>

            <div className={`overflow-x-auto rounded-xl border ${isDark ? 'border-white/20 bg-white/10 backdrop-blur-sm' : 'border-gray-200 bg-white'} shadow-[0_4px_6px_rgba(0,0,0,0.05)] mt-4`}>
              <table className={`w-full border-collapse my-4 rounded-xl overflow-hidden shadow-[0_4px_6px_rgba(0,0,0,0.05)] border ${isDark ? 'border-white/20' : 'border-gray-200'} md:text-sm`}>
                <thead>
                  <tr>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Title
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Category
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Price
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Duration
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Status
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Featured
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Created
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((service) => (
                    <tr key={service.id} className={isDark ? "hover:bg-white/10" : "hover:bg-gray-50"}>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <div>
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {getLocalizedString(service.title, "en")}
                          </div>
                          <div className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'} line-clamp-2`}>
                            {getLocalizedString(service.shortDescription, "en")}
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {service.category}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        ${service.price}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {service.duration}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
                            service.published
                              ? isDark ? "bg-green-500/80 text-white" : "bg-green-100 text-green-800"
                              : isDark ? "bg-yellow-500/80 text-white" : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {service.published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
                            service.featured
                              ? isDark ? "bg-green-500/80 text-white" : "bg-green-100 text-green-800"
                              : isDark ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {service.featured ? "Featured" : "Regular"}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {service.createdAt?.toDate().toLocaleDateString()}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <div className="flex gap-2 flex-wrap items-center md:flex-col">
                          <button
                            className={`px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm ${isDark ? 'bg-white/20 text-white border border-white/30 hover:bg-white/30' : 'bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100'} hover:-translate-y-0.5`}
                            onClick={() =>
                              navigate(`/admin/service/edit/${service.id}`)
                            }
                          >
                            Edit
                          </button>
                          <button
                            className={`px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm ${
                              service.published
                                ? "bg-amber-500 text-white hover:bg-amber-600"
                                : "bg-green-500 text-white hover:bg-green-600"
                            } hover:-translate-y-0.5`}
                            onClick={() => togglePublishService(service)}
                          >
                            {service.published ? "Unpublish" : "Publish"}
                          </button>
                          <button
                            className="px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm bg-gradient-to-r from-red-500 to-red-600 text-white shadow-[0_2px_4px_rgba(239,68,68,0.2)] hover:from-red-600 hover:to-red-700 hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(239,68,68,0.3)]"
                            onClick={() => handleDeleteService(service.id!)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "products" && (
          <div className="w-full">
            <div className={`flex justify-between items-center mb-8 py-6 border-b-2 ${isDark ? 'border-white/20' : 'border-gray-200'} rounded-t-xl px-8 -mx-8 -mt-8 md:flex-col md:items-start md:gap-4 md:px-4`}>
              <h2 className={`${isDark ? 'text-white' : 'text-gray-900'} text-2xl font-bold m-0 flex items-center gap-3 before:content-[''] before:w-1 before:h-6 ${isDark ? 'before:bg-white' : 'before:bg-primary'} before:rounded-sm`}>
                Products Management
              </h2>
              <button
                className="px-5 py-2.5 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm bg-[var(--gradient-primary)] text-black shadow-[0_2px_4px_rgba(37,99,235,0.2)] hover:bg-[var(--gradient-secondary)] hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(37,99,235,0.3)]"
                onClick={() => navigate("/admin/product/new")}
              >
                Add New Product
              </button>
            </div>

            <div className={`overflow-x-auto rounded-xl border ${isDark ? 'border-white/20 bg-white/10 backdrop-blur-sm' : 'border-gray-200 bg-white'} shadow-[0_4px_6px_rgba(0,0,0,0.05)] mt-4`}>
              <table className={`w-full border-collapse my-4 rounded-xl overflow-hidden shadow-[0_4px_6px_rgba(0,0,0,0.05)] border ${isDark ? 'border-white/20' : 'border-gray-200'} md:text-sm`}>
                <thead>
                  <tr>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Title
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Category
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Price
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Stock
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Status
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Featured
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Created
                    </th>
                    <th className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-700'} font-semibold text-xs uppercase tracking-wider`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className={isDark ? "hover:bg-white/10" : "hover:bg-gray-50"}>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <div>
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {getLocalizedString(product.title, "en")}
                          </div>
                          <div className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'} line-clamp-2`}>
                            {getLocalizedString(product.shortDescription, "en")}
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {product.category}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {product.price}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
                            product.inStock
                              ? isDark ? "bg-green-500/80 text-white" : "bg-green-100 text-green-800"
                              : isDark ? "bg-yellow-500/80 text-white" : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {product.inStock
                            ? `In Stock (${product.stockQuantity})`
                            : "Out of Stock"}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
                            product.published
                              ? isDark ? "bg-green-500/80 text-white" : "bg-green-100 text-green-800"
                              : isDark ? "bg-yellow-500/80 text-white" : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {product.published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
                            product.featured
                              ? isDark ? "bg-green-500/80 text-white" : "bg-green-100 text-green-800"
                              : isDark ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {product.featured ? "Featured" : "Regular"}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20 text-white' : 'border-gray-200 text-gray-900'} last:border-b-0`}>
                        {product.createdAt?.toDate().toLocaleDateString()}
                      </td>
                      <td className={`px-6 py-4 text-left border-b ${isDark ? 'border-white/20' : 'border-gray-200'} last:border-b-0`}>
                        <div className="flex gap-2 flex-wrap items-center md:flex-col">
                          <button
                            className={`px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm ${isDark ? 'bg-white/20 text-white border border-white/30 hover:bg-white/30' : 'bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100'} hover:-translate-y-0.5`}
                            onClick={() =>
                              navigate(`/admin/product/edit/${product.id}`)
                            }
                          >
                            Edit
                          </button>
                          <button
                            className={`px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm ${
                              product.published
                                ? "bg-amber-500 text-white hover:bg-amber-600"
                                : "bg-green-500 text-white hover:bg-green-600"
                            } hover:-translate-y-0.5`}
                            onClick={() => togglePublishProduct(product)}
                          >
                            {product.published ? "Unpublish" : "Publish"}
                          </button>
                          <button
                            className="px-4 py-2 rounded-lg border-none cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 text-center min-h-[36px] text-sm bg-gradient-to-r from-red-500 to-red-600 text-white shadow-[0_2px_4px_rgba(239,68,68,0.2)] hover:from-red-600 hover:to-red-700 hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(239,68,68,0.3)]"
                            onClick={() => handleDeleteProduct(product.id!)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "hero" && <HeroAdmin />}
      </div>
    </div>
  );
};

export default Admin;
